import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import * as ActionCable from 'actioncable';

/**
 * Broadcaster - manages an internal event bus for ActionCable channel messages.
 * Replaces the `Broadcaster` class from the abandoned `ngx-cable` package.
 */
@Injectable()
export class Broadcaster {
  private _eventBus: Subject<{ key: string; data: any }>;

  constructor() {
    this.init();
  }

  private init(): void {
    this._eventBus = new Subject<{ key: string; data: any }>();
  }

  public broadcast(key: string, data: any): void {
    this._eventBus.next({ key, data });
  }

  public on(key: string): Observable<any> {
    return this._eventBus.asObservable().pipe(
      filter((event) => event.key === key),
      map((event) => event.data)
    );
  }

  public off(): void {
    this.init();
  }
}

/**
 * NgXCable - wraps the `actioncable` npm package for Angular.
 * Replaces the `NgXCable` class from the abandoned `ngx-cable` package.
 *
 * Provides the same public API:
 *   connect(url), create(params), subscribe(params), send(data),
 *   perform(action, data), unsubscribe(), getSubscriptions(),
 *   getCountSubscriptions(), searchSubcriptions(id, field),
 *   reject(subscription), disconnect(), isOpen()
 *
 * Lifecycle rules (the historical version broke every one of these, which
 * leaked one open WebSocket per candidate page / sidebar visit):
 *
 * - One consumer per socket URL, shared by every NgXCable instance. The
 *   service is module-provided, so lazy modules get their own instance, but
 *   they must not each open their own socket.
 * - `connect()` is idempotent for the same URL; a different URL closes the
 *   previous socket first.
 * - The socket is closed as soon as its last subscription is removed and
 *   reopened on the next `create()`, so leaving the candidate area leaves no
 *   socket, no 3-second monitor timer and no `visibilitychange` listener behind.
 * - The socket is opened outside the Angular zone: the server ping every
 *   3 seconds and the client monitor poll must not trigger app-wide change
 *   detection. Messages re-enter the zone via `broadcast`.
 * - Each instance remembers the subscriptions it created, so a component
 *   calling `unsubscribe()` with no arguments only removes its own instead of
 *   wiping another component's subscriptions on the shared socket.
 */
@Injectable()
export class NgXCable implements OnDestroy {
  private static consumers = new Map<string, any>();

  private cable: any;
  private url: string;
  private created: any[] = [];

  constructor(
    private readonly broadcaster: Broadcaster,
    private readonly zone: NgZone
  ) {}

  /** Test seam: forget shared consumers between specs. */
  public static resetForTesting(): void {
    NgXCable.consumers.clear();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public setCable(url: string): void {
    this.connect(url);
  }

  public connect(url: string): void {
    const existing = NgXCable.consumers.get(url);

    if (existing) {
      this.cable = existing;
      this.url = url;
      return;
    }

    if (this.cable && this.url !== url) {
      this.disconnect();
    }

    // A consumer another instance already closed: forget it, keep `created`
    // (those subscriptions are gone from the socket too) and open a new one.
    this.cable = undefined;
    this.created = [];

    this.cable = this.zone.runOutsideAngular(() => this.createConsumer(url));
    this.url = url;
    NgXCable.consumers.set(url, this.cable);
  }

  public isOpen(): boolean {
    if (this.cable) {
      return !this.cable.connection.disconnected;
    }
    return false;
  }

  public create(params: { channel: string; room: string }): any {
    // Re-resolve through the shared map: another instance may have closed
    // the consumer this one still holds, in which case a fresh one is opened
    // and registered instead of reviving the closed socket outside the map.
    if (this.url) {
      this.connect(this.url);
    }

    const subscription = this.zone.runOutsideAngular(() =>
      this.cable.subscriptions.create(params, {
        received: (data: any) => {
          this.zone.run(() => this.broadcaster.broadcast(params.channel, data));
        }
      })
    );

    this.created.push(subscription);

    return subscription;
  }

  public subscribe(params: { channel: string; room: string }): any {
    return this.create(params);
  }

  public send(data: any, subscriptions?: any[]): boolean {
    if (!this.isOpen()) {
      return false;
    }

    if (subscriptions == null) {
      const target = this.created[0] ?? this.cable.subscriptions.subscriptions[0];
      if (!target) {
        return false;
      }
      target.send(data);
    } else if (subscriptions instanceof Array) {
      subscriptions.forEach((subscription) => {
        subscription.send(data);
      });
    } else {
      return false;
    }

    return true;
  }

  public perform(action: string, data: any, subscriptions?: any[]): boolean {
    if (!this.isOpen()) {
      return false;
    }

    if (subscriptions == null) {
      const target = this.created[0] ?? this.cable.subscriptions.subscriptions[0];
      if (!target) {
        return false;
      }
      target.perform(action, data);
    } else if (subscriptions instanceof Array) {
      subscriptions.forEach((subscription) => {
        subscription.perform(action, data);
      });
    } else {
      return false;
    }

    return true;
  }

  /**
   * Removes subscriptions from the shared socket. With no argument, removes
   * the ones this instance created. Closes the socket when none remain.
   */
  public unsubscribe(subscriptions?: any[]): boolean {
    if (!this.cable) {
      return false;
    }

    let targets: any[];
    if (subscriptions == null) {
      targets = [...this.created];
    } else if (subscriptions instanceof Array) {
      targets = subscriptions;
    } else {
      return false;
    }

    targets.forEach((subscription) => this.remove(subscription));
    this.releaseIfIdle();

    return true;
  }

  public getSubscriptions(): any[] {
    return this.cable ? this.cable.subscriptions.subscriptions : [];
  }

  public getCountSubscriptions(): number {
    return this.getSubscriptions().length;
  }

  public searchSubcriptions(id: string, field: string = 'room'): any[] {
    const rsub: any[] = [];

    this.getSubscriptions().forEach((subscription) => {
      const msg = JSON.parse(subscription.identifier);
      if (id === msg[field]) {
        rsub.push(subscription);
      }
    });

    return rsub;
  }

  public reject(subscription: any): boolean {
    if (subscription && this.cable) {
      this.remove(subscription);
      this.releaseIfIdle();
      return true;
    }
    return false;
  }

  public disconnect(): boolean {
    if (!this.cable) {
      return false;
    }

    const cable = this.cable;
    const url = this.url;

    if (NgXCable.consumers.get(url) === cable) {
      NgXCable.consumers.delete(url);
    }

    this.cable = undefined;
    this.created = [];

    // `disconnect()` stops the ConnectionMonitor (timer + visibilitychange
    // listener) and closes the socket without reconnecting.
    this.zone.runOutsideAngular(() => cable.disconnect());

    return true;
  }

  /** Seam for tests; production always builds a real actioncable consumer. */
  protected createConsumer(url: string): any {
    return ActionCable.createConsumer(url);
  }

  private remove(subscription: any): void {
    this.created = this.created.filter((s) => s !== subscription);

    if (this.cable.subscriptions.subscriptions.includes(subscription)) {
      this.zone.runOutsideAngular(() =>
        this.cable.subscriptions.remove(subscription)
      );
    }
  }

  private releaseIfIdle(): void {
    if (this.cable && this.cable.subscriptions.subscriptions.length === 0) {
      this.disconnect();
    }
  }
}
